import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import Modal from '../../components/common/model/Modal';
import styles from './ProductList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import productService from '../../services/productService';
import InputField from '../../components/common/inputField/InputField';
import { Product } from '../../types/productTypes';

const ProductList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    price: '',
    full_stock_count: 0,
    empty_stock_count: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);

  const fetchProducts = async () => {
  try {
    const response = await productService.getProducts();

    const normalizedProducts: Product[] = Array.isArray(response)
      ? response
          .map((product): Product | null => {
            if (!product || typeof product !== 'object') {
              console.warn('Invalid product object:', product);
              return null;
            }

            return {
              id: product.id ?? '',
              name: product.name ?? '',
              price: Number(product.price) || 0,
              employee_id: product.employee_id ?? '',
              employee_name: product.employee_name,
              full_stock_count: product.full_stock_count,
              empty_stock_count: product.empty_stock_count,
              is_active: product.is_active ?? true,
              created_at: product.created_at ?? new Date().toISOString(),
              updated_at: product.updated_at ?? new Date().toISOString(),
            };
          })
          .filter((product): product is Product => product !== null)
      : [];

    setProducts(normalizedProducts);
  } catch (err) {
    console.error('Error fetching products:', err);
    toast.error('Failed to fetch products');
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchProducts();
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim() || !form.price.trim()) {
      toast.error('Please fill in all required fields: Name and Price.');
      return;
    }

    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Please enter a valid price.');
      return;
    }
    
    const data = {
      name: form.name,
      price: priceNum,
      full_stock_count: form.full_stock_count,
      empty_stock_count: form.empty_stock_count
    };

    try {
      await productService.createProduct(data); 
      toast.success('Product created successfully!');
      setShowModal(false);
      resetForm();
      fetchProducts(); 
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create product';
      toast.error(message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product? This will also delete related stock and purchase records.')) {
      return;
    }

    try {
      await productService.deleteProduct(id);
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleActive = async (id: string) => {
    const currentProduct = products.find(product => product.id === id);
    if (!currentProduct) {
      toast.error('Product not found');
      return;
    }

    const currentStatus = currentProduct.is_active ?? true;
    const newStatus = !currentStatus;
    
    setProducts(products.map(product =>
      product.id === id ? { ...product, is_active: newStatus } : product
    ));

    try {
      const updatedProduct = await productService.toggleProductStatus(id);
      
      if (updatedProduct && typeof updatedProduct === 'object') {
        setProducts(prevProducts => prevProducts.map(product => {
          if (product.id === id) {
            return {
              ...product,
              ...updatedProduct,
              is_active: updatedProduct.is_active ?? newStatus,
            };
          }
          return product;
        }));
      }
      
      toast.success('Product status updated successfully!');
    } catch (err) {
      setProducts(prevProducts => prevProducts.map(product =>
        product.id === id ? { ...product, is_active: currentStatus } : product
      ));
      
      const message = err instanceof Error ? err.message : 'Failed to update product status';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      price: '',
      full_stock_count: 0,
    empty_stock_count: 0
    });
  }

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleOpenEditModal = (product: Product) => {
    setForm({
      name: product.name || '',
      price: product.price.toString() || '',
      full_stock_count: product.full_stock_count || 0,
    empty_stock_count: product.empty_stock_count ||0
    });
    setEditingProductId(product.id);
    setEditMode(true);
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim() || !form.price.trim()) {
      toast.error('Please fill in all required fields: Name and Price.');
      return;
    }

    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Please enter a valid price.');
      return;
    }

    if (!editingProductId) return;
    
    const data = {
      id: editingProductId,
      name: form.name,
      price: priceNum,
      full_stock_count: form.full_stock_count,
      empty_stock_count: form.empty_stock_count
    };
    
    try {
      await productService.updateProduct(data);
      toast.success('Product updated successfully!');
      setShowModal(false);
      setEditMode(false);
      setEditingProductId(null);
      resetForm();
      fetchProducts();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update product';
      toast.error(message);
    }
  };

  const filteredProducts = products.filter(product =>
    (product.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (product.employee_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  return (
    <div className={styles.container}>
      <h4>Products</h4>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search products by name or creator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          {role === 'admin' && (
            <Button variant="primary" onClick={handleOpenModal}>
              + Add Product
            </Button>
          )}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Price</th>
              <th>Full Stock</th>
              <th>Empty Stock</th>
              <th>Created By</th>
              {role === 'admin' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={role === 'admin' ? 6 : 5} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading products...
                </td>
              </tr>
            ) : (
              Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
                filteredProducts.map(product => {
                  if (!product || !product.id) {
                    console.warn('Invalid product in render:', product);
                    return null;
                  }
                  
                  const isActive = product.is_active !== undefined ? product.is_active : true;
                  
                  return (
                    <tr key={product.id} className={!isActive ? styles.inactive : ''}>
                      <td>{product.name || '-'}</td>
                      <td>{formatPrice(product.price)}</td>
                      <td>{product.full_stock_count ?? 0}</td>
                      <td>{product.empty_stock_count ?? 0}</td>
                      <td>{product.employee_name || '-'}</td>
                      {role === 'admin' && (
                        <td>
                          <div className={styles.actionIcons}>
                            <Button
                              variant={isActive ? 'secondary' : 'success'}
                              className={styles.actionButton}
                              onClick={() => handleToggleActive(product.id)}
                              aria-label={isActive ? 'Disable Product' : 'Enable Product'}
                            >
                              {isActive ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleOpenEditModal(product)}
                              className={styles.actionButton}
                              aria-label="Edit Product"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9"/>
                                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/>
                              </svg>
                            </Button>
                            <Button
                              onClick={() => handleDelete(product.id)}
                              className={styles.actionButton}
                              aria-label="Delete Product"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                }).filter(row => row !== null)
              ) : (
                <tr>
                  <td colSpan={role === 'admin' ? 6 : 5} style={{ textAlign: 'center', padding: '20px' }}>
                    {searchTerm ? 'No products found matching your search' : 'No products found'}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      
      <Modal 
        isOpen={showModal} 
        onClose={() => { 
          setShowModal(false); 
          setEditMode(false); 
          setEditingProductId(null); 
          resetForm(); 
        }} 
        title={editMode ? "Edit Product" : "Add Product"} 
        size='lg'
      >
        <div className={styles.modalForm}>
          <InputField
            label="Product Name"
            name="name"
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Enter product name"
            required
          />
          <InputField
            label="Price"
            name="price"
            type="number"
            value={form.price}
            onChange={e => setForm({ ...form, price: e.target.value })}
            placeholder="Enter price"
            required
          />

          <InputField
            label="Full Stock Count"
            name="full_stock_count"
            type="number"
            value={form.full_stock_count}
            onChange={e => setForm({ ...form, full_stock_count: Number(e.target.value) })}
            placeholder="Enter full stock count"
            required
          />

          <InputField
            label="Empty Stock Count"
            name="empty_stock_count"
            type="number"
            value={form.empty_stock_count}
            onChange={e => setForm({ ...form, empty_stock_count: Number(e.target.value) })}
            placeholder="Enter empty stock count"
            required
          />

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditMode(false);
                setEditingProductId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={editMode ? handleSaveEdit : handleAdd}
              className={styles.submitButton}
            >
              {editMode ? 'Save Changes' : 'Add Product'}
            </Button>
          </div>
        </div>
      </Modal>
      
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default ProductList;